import {assoc, dissoc, reject, filter, isNil, map, values, sortWith} from 'ramda';
import moment from 'moment';
import {Commit} from "vuex";
import Vue from 'vue';
import {
  IActiveShipmentStore,
  IActionsSignature,
  IActiveShipment,
  IDefinePickup,
  IActiveOrderAwaitingAndOtherLists,
} from "@/models/StoreModels";
import * as services from "@/services/services";
import {ShipmentLogic} from "@/models/ShipmentLogic";

const initialState = (): IActiveShipmentStore => {
  return {
    activeShipmentsOrderedById: [],
    activeShipments: {},
  };
};

const aShipmentDiff = (a: IActiveShipment, b: IActiveShipment): number => {
  const firstShipment = moment(a.updated_at);
  const secondShipment = moment(b.updated_at);
  const timeBetweenShipments = moment.duration(secondShipment.diff(firstShipment));

  return timeBetweenShipments.asSeconds();
};

const shipmentIsLessThanTwoHours = (currentShipment: IActiveShipment): boolean => {
  const now = moment();
  const shipment = moment(currentShipment.updated_at);
  const timeSinceShipment = moment.duration(now.diff(shipment));

  return timeSinceShipment.asHours() < 2;
};

const markEntriesLessTwoHours = (myShipments: IActiveShipment[]) => {
  let i: number = 0;
  let lastEntryFound: boolean = true;

  while (i < myShipments.length && lastEntryFound) {
    const currentShipment = myShipments[i];
    lastEntryFound = !shipmentIsLessThanTwoHours(currentShipment);
    currentShipment.recently_updated = !lastEntryFound;
    i++;
  }
};

const getters = {
  /**
   * return an ordered shipmentLIst list
   */
  getActiveOrderList(state: IActiveShipmentStore): IActiveShipment[] {
    return map(id => state.activeShipments[`${id}`], state.activeShipmentsOrderedById);

    // the ones that need to be picked up must be shown first
    // the one the user created must be first
  },


  getActiveOrderListsAwaitingAndOther(state: IActiveShipmentStore): IActiveOrderAwaitingAndOtherLists {
    let awaitingDropoffOrders: IActiveShipment[] = [];
    let otherOrders: IActiveShipment[] = [];
    let activeShipmentList = values(state.activeShipments);

    activeShipmentList = sortWith([aShipmentDiff], activeShipmentList);
    awaitingDropoffOrders = filter(ShipmentLogic.isAwaitingDropoff, activeShipmentList);
    otherOrders = reject(ShipmentLogic.isAwaitingDropoff, activeShipmentList);

    markEntriesLessTwoHours(awaitingDropoffOrders);
    markEntriesLessTwoHours(otherOrders);

    return { awaitingDropoff: awaitingDropoffOrders, others: otherOrders};
    // the ones that need to be picked up must be shown first
    // the one the user created must be first
  },

  /**
   * return an ordered shipmentLIst list
   */
  getActiveOrderListInversed(state: IActiveShipmentStore) {
    const myList: IActiveShipment[] = getters.getActiveOrderList(state);
    return myList.reverse();
  },

  getActiveOrderById(state: IActiveShipmentStore): (aoId: number|string) => IActiveShipment {
    return (aoId: number|string): IActiveShipment => {
      return state.activeShipments[aoId];
    };
  },

};


const actions = {
  reset({commit, state}: IActionsSignature<IActiveShipmentStore>) {
    commit('reset');
  },

  init({commit}: { commit: Commit }): Promise<any> {
    return services.apiCaller.GET("/shipments/active", [])
      .then((myShipments) => {
        commit('updateActiveShipments', myShipments);
      })
      .catch(err => 'fail init promotions');
  },


  cancelOrder({commit, state}: IActionsSignature<IActiveShipmentStore>, asId: number|string): Promise<any> {
    return services.apiCaller.POST(`/shipments/${asId}/cancel`, [])
      .then((myShipments) => {
        commit('updateActiveShipments', myShipments);
      });
  },

  setPickupHub({commit, state}: IActionsSignature<IActiveShipmentStore>, {asId, hubId}: IDefinePickup) {
    return services.apiCaller.PUT(`/shipments/${asId}/destination`, { hub_id: hubId })
      // todo: check the correct body
      .then((myShipment: IActiveShipment) => {
        commit('updateSingleActiveShipment', myShipment);
      });
  },

  pickupShipment({commit, state}: IActionsSignature<IActiveShipmentStore>, asId: number|string): Promise<any> {
    return services.apiCaller.POST(`/shipments/${asId}/pickup`)
      .then((activeShipment: IActiveShipment) => {
        commit('removeActiveShipment', asId); // we need to remove it because, after picking the package, the
        // shipment is done.
      });
  },

  depositShipment({commit, state}: IActionsSignature<IActiveShipmentStore>, asId: number|string): Promise<any>  {
    return services.apiCaller.POST(`/shipments/${asId}/deposit`)
      .then((activeShipment: IActiveShipment) => {
        commit('removeActiveShipment', asId); // in both open we should remove the active shipping
        // in this case is because, after we deposit what we want, the shipment will be seen by the target user, not
        // the sender.
      });
  },

  reOpenShipment({commit, state}: IActionsSignature<IActiveShipmentStore>, asId: number|string): Promise<any>  {
    return services.apiCaller.POST(`/shipments/${asId}/reOpen`);
  },
};


const mutations = {
  removeActiveShipment(state: IActiveShipmentStore, asId: number|string) {
    if (asId) {
      asId = `${asId}`;
      let activeShipmentsIds = state.activeShipmentsOrderedById;
      let activeShipmentsLibrary = state.activeShipments;

      activeShipmentsIds = reject(el => `${el}` === asId, activeShipmentsIds);
      activeShipmentsLibrary = dissoc(asId, activeShipmentsLibrary);

      Vue.set(state, "activeShipmentsOrderedById", activeShipmentsIds);
      Vue.set(state, "activeShipments", activeShipmentsLibrary);
    }
  },

  addActiveShipment(state: IActiveShipmentStore, actShip: IActiveShipment) {
    if (actShip) {
      actShip.recently_updated = false;
      const asId: number = actShip.id;
      const activeShipmentsIds = state.activeShipmentsOrderedById;
      let activeShipmentsLibrary = state.activeShipments;

      activeShipmentsIds.push(asId);
      activeShipmentsLibrary = assoc(`${asId}`, actShip, activeShipmentsIds);

      Vue.set(state, "activeShipmentsOrderedById", activeShipmentsIds);
      Vue.set(state, "activeShipments", activeShipmentsLibrary);
    }
  },

  modifyTargetHubId(state: IActiveShipmentStore, {asId, hubId}: IDefinePickup) {
    const activeShipmentsLibrary = state.activeShipments;
    activeShipmentsLibrary[`${asId}`].to_cell_id = hubId; // in the future will be to_hub_id;

    Vue.set(state, "activeShipments", activeShipmentsLibrary);
  },

  updateActiveShipments(state: IActiveShipmentStore, activeShipments: IActiveShipment[]) {
    if (!isNil(activeShipments)) {
      let activeShipmentsLibrary = {};

      const activeShipmentsIds: number[] = map((activeShipment: IActiveShipment) => {
        const currentId = activeShipment.id;
        activeShipment.recently_updated = false;
        activeShipmentsLibrary = assoc(`${currentId}`, activeShipment, activeShipmentsLibrary);
        return currentId;
      }, activeShipments);

      Vue.set(state, "activeShipmentsOrderedById", activeShipmentsIds);
      Vue.set(state, "activeShipments", activeShipmentsLibrary);
    }
  },

  updateSingleActiveShipment(state: IActiveShipmentStore, activeShipment: IActiveShipment) {
    const asId: string = `${ activeShipment.id}`;
    const activeShipmentsLibrary: {[key: string]: IActiveShipment} = state.activeShipments;
    activeShipment.recently_updated = false;

    if (!isNil(activeShipment) && activeShipmentsLibrary[asId]) {
      activeShipmentsLibrary[activeShipment.id] = activeShipment ;

      Vue.set(state, "activeShipments", activeShipmentsLibrary);
    }
  },

  reset(state: IActiveShipmentStore) {
    state = Object.assign(state, initialState());
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
